import { useReducer, useCallback } from 'react';
import type {
  OnboardingState,
  OnboardingAction,
  PreviewData,
  ValidationError,
  ExecuteResponse,
} from '../types';

const initialState: OnboardingState = {
  step: 'select-org',
  organisationId: null,
  organisationName: null,
  file: null,
  previewData: null,
  parseWarnings: [],
  validationErrors: [],
  isValidated: false,
  executionProgress: null,
  executionResult: null,
};

function reducer(
  state: OnboardingState,
  action: OnboardingAction
): OnboardingState {
  switch (action.type) {
    case 'SELECT_ORGANISATION':
      return {
        ...state,
        organisationId: action.payload.id,
        organisationName: action.payload.name,
        step: state.file ? 'upload' : 'select-org',
      };

    case 'SELECT_FILE':
      return {
        ...state,
        file: action.payload,
        step: state.organisationId ? 'upload' : 'select-org',
        // Reset downstream state when new file is selected
        previewData: null,
        parseWarnings: [],
        validationErrors: [],
        isValidated: false,
        executionProgress: null,
        executionResult: null,
      };

    case 'CLEAR_FILE':
      return {
        ...state,
        file: null,
        step: state.organisationId ? 'upload' : 'select-org',
        previewData: null,
        parseWarnings: [],
        validationErrors: [],
        isValidated: false,
        executionProgress: null,
        executionResult: null,
      };

    case 'PARSE_SUCCESS':
      return {
        ...state,
        previewData: action.payload,
        parseWarnings: action.payload.warnings || [],
        step: 'preview',
        validationErrors: [],
        isValidated: false,
      };

    case 'UPDATE_PREVIEW':
      return {
        ...state,
        previewData: action.payload,
        // Invalidate validation when preview is edited
        isValidated: false,
        validationErrors: [],
      };

    case 'VALIDATE_START':
      return {
        ...state,
        step: 'validating',
        validationErrors: [],
      };

    case 'VALIDATE_SUCCESS':
      return {
        ...state,
        step: 'validated',
        isValidated: true,
        validationErrors: [],
      };

    case 'VALIDATE_FAILURE':
      return {
        ...state,
        step: 'preview',
        isValidated: false,
        validationErrors: action.payload,
      };

    case 'EXECUTE_START':
      return {
        ...state,
        step: 'executing',
        executionProgress: { currentStep: 0, stepLabel: '' },
        executionResult: null,
      };

    case 'EXECUTE_PROGRESS':
      return {
        ...state,
        executionProgress: {
          currentStep: action.payload.step,
          stepLabel: action.payload.label,
        },
      };

    case 'EXECUTE_SUCCESS':
      return {
        ...state,
        step: 'success',
        executionProgress: null,
        executionResult: action.payload,
      };

    case 'EXECUTE_FAILURE':
      return {
        ...state,
        step: 'error',
        executionProgress: null,
        executionResult: action.payload,
      };

    case 'BACK_TO_PREVIEW':
      return {
        ...state,
        step: 'preview',
        executionProgress: null,
        executionResult: null,
        isValidated: false,
        validationErrors: [],
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

export function useOnboardingState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectOrganisation = useCallback((id: string, name: string) => {
    dispatch({ type: 'SELECT_ORGANISATION', payload: { id, name } });
  }, []);

  const selectFile = useCallback((file: File) => {
    dispatch({ type: 'SELECT_FILE', payload: file });
  }, []);

  const clearFile = useCallback(() => {
    dispatch({ type: 'CLEAR_FILE' });
  }, []);

  const parseSuccess = useCallback((data: PreviewData) => {
    dispatch({ type: 'PARSE_SUCCESS', payload: data });
  }, []);

  const updatePreview = useCallback((data: PreviewData) => {
    dispatch({ type: 'UPDATE_PREVIEW', payload: data });
  }, []);

  const startValidation = useCallback(() => {
    dispatch({ type: 'VALIDATE_START' });
  }, []);

  const validationSuccess = useCallback(() => {
    dispatch({ type: 'VALIDATE_SUCCESS' });
  }, []);

  const validationFailure = useCallback((errors: ValidationError[]) => {
    dispatch({ type: 'VALIDATE_FAILURE', payload: errors });
  }, []);

  const startExecution = useCallback(() => {
    dispatch({ type: 'EXECUTE_START' });
  }, []);

  const updateExecutionProgress = useCallback((step: number, label: string) => {
    dispatch({ type: 'EXECUTE_PROGRESS', payload: { step, label } });
  }, []);

  const executionSuccess = useCallback((result: ExecuteResponse) => {
    dispatch({ type: 'EXECUTE_SUCCESS', payload: result });
  }, []);

  const executionFailure = useCallback((result: ExecuteResponse) => {
    dispatch({ type: 'EXECUTE_FAILURE', payload: result });
  }, []);

  const backToPreview = useCallback(() => {
    dispatch({ type: 'BACK_TO_PREVIEW' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    actions: {
      selectOrganisation,
      selectFile,
      clearFile,
      parseSuccess,
      updatePreview,
      startValidation,
      validationSuccess,
      validationFailure,
      startExecution,
      updateExecutionProgress,
      executionSuccess,
      executionFailure,
      backToPreview,
      reset,
    },
  };
}
